import React from 'react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartProps } from '../../types/visualization';
import CustomTooltip from '../common/CustomTooltip';

const DEFAULT_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b'];

const BarChart: React.FC<ChartProps> = ({ 
  data, 
  config, 
  colors = DEFAULT_COLORS, 
  height = 350 
}) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Get chart configuration
  const dataKeys = Object.keys(data[0]);
  const xAxisColumn = config?.xAxis?.column || dataKeys[0];
  const primaryColumns = config?.yAxis?.primary?.columns || [];
  const primaryLabels = config?.yAxis?.primary?.labels || primaryColumns;

  // Fallback: auto-detect Y column if not configured
  const yColumn = primaryColumns[0] || 
    dataKeys.find(k => k !== xAxisColumn && typeof data[0][k] === 'number') || 
    dataKeys[1];

  // If yColumn is still not found, try to find any numeric column
  const finalYColumn = yColumn || dataKeys.find(k => typeof data[0][k] === 'number') || dataKeys[1];

  // Process data to convert currency strings to numbers
  const processedData = data.map(item => {
    const processedItem = { ...item };
    
    // Convert currency strings to numbers for Y column
    if (finalYColumn && typeof processedItem[finalYColumn] === 'string') {
      const value = processedItem[finalYColumn];
      // Remove currency symbols and commas, then convert to number
      const numericValue = parseFloat(value.replace(/[$,]/g, ''));
      if (!isNaN(numericValue)) {
        processedItem[finalYColumn] = numericValue;
      }
    }
    
    return processedItem;
  });

  // Check if we have valid data for the chart
  const hasValidData = processedData.some(item => 
    item[finalYColumn] !== undefined && 
    item[finalYColumn] !== null && 
    !isNaN(Number(item[finalYColumn]))
  );

  if (!hasValidData) {
    return <div>No valid numeric data available for chart</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart 
        data={processedData} 
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey={xAxisColumn}
          angle={-45}
          textAnchor="end"
          height={80}
          fontSize={12}
        />
        <YAxis 
          fontSize={12}
          tickFormatter={(value) => {
            if (config?.yAxis?.primary?.format === 'currency') {
              return `$${value.toLocaleString()}`;
            }
            return value.toLocaleString();
          }}
        />
        <Tooltip 
          content={<CustomTooltip 
            config={config} 
            xAxisLabel={config?.xAxis?.label || 'Category'} 
            currencyFormat={config?.yAxis?.primary?.format === 'currency'}
          />} 
        />
        <Bar 
          dataKey={finalYColumn} 
          fill={colors[0]} 
          name={primaryLabels[0] || finalYColumn}
        />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};

export default BarChart; 